import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { LayoutGrid, TrendingUp, Star, Target, ClipboardCheck } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import StatCard from '../../components/ui/StatCard';
import GaugeMeter from '../../components/ui/GaugeMeter';
import { sessionService } from '../../services';
import api from '../../services/api';
import {
  getMetricBandInfo,
  getTRSInterpretation,
  getARSInterpretation,
  getLAIInterpretation,
  getTEIInterpretation,
  getCRIInterpretation,
} from '../../utils/reportingConstants';

/**
 * Trainer Session Analytics Page
 * Mirrors the session-related sections from ClientAnalyticsPage but scoped to
 * the trainer's own sessions. Used by solo (individual) trainers who own their
 * org's data.
 */

const TIME_RANGE_OPTIONS = [
  { value: '7d', label: '7 Days' },
  { value: '14d', label: '14 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: 'all', label: 'All Time' },
];

const periodToDays = (range) => {
  switch (range) {
    case '7d': return 7;
    case '14d': return 14;
    case '30d': return 30;
    case '90d': return 90;
    case 'all': return null;
    default: return 30;
  }
};

// ----- per-session calculators (kept identical to ClientAnalyticsPage) -----

const calculateEngagement = (session) => {
  const participants = session.participants?.filter(p => p.status === 'joined').length || 0;
  if (participants === 0) return 0;

  const allActivities = session.subSessions?.flatMap(sub => sub.activities || []) || [];
  const launchedActivities = allActivities.filter(a =>
    a.status === 'launched' || a.status === 'completed' || a.status === 'active'
  );
  if (launchedActivities.length === 0) return 0;

  const totalResponses = session.computedTotalResponses != null
    ? session.computedTotalResponses
    : launchedActivities.reduce((sum, a) => sum + (a.responses?.length || 0), 0);
  const maxPossibleResponses = launchedActivities.length * participants;

  return maxPossibleResponses > 0
    ? Math.min(100, Math.round((totalResponses / maxPossibleResponses) * 100))
    : 0;
};

const calculateCompletion = (session) => {
  const allActivities = session.subSessions?.flatMap(sub => sub.activities || []) || [];
  if (allActivities.length === 0) return 0;
  const completedActivities = allActivities.filter(a => a.status === 'completed').length;
  return Math.round((completedActivities / allActivities.length) * 100);
};

const calculateRating = (session) => {
  const allActivities = session.subSessions?.flatMap(sub => sub.activities || []) || [];
  const feedbackActivities = allActivities.filter(a => a.type === 'feedback');
  if (feedbackActivities.length === 0) return 0;

  const emojiRatings = { '😞': 1, '😑': 2, '🙂': 3, '😄': 4, '🤩': 5 };
  let totalRating = 0;
  let ratingCount = 0;

  feedbackActivities.forEach(activity => {
    (activity.responses || []).forEach(response => {
      const resp = response.response;
      let rating = null;
      if (typeof resp?.rating === 'number') rating = resp.rating;
      else if (typeof resp?.score === 'number') rating = resp.score;
      else if (typeof resp?.value === 'number') rating = resp.value;
      else if (typeof response?.rating === 'number') rating = response.rating;
      else if (resp?.emoji || resp?.selectedEmoji) {
        rating = emojiRatings[resp.emoji || resp.selectedEmoji] || null;
      } else if (typeof resp === 'number') rating = resp;
      if (rating && rating > 0 && rating <= 5) {
        totalRating += rating;
        ratingCount++;
      }
    });
  });

  return ratingCount > 0 ? Math.round((totalRating / ratingCount) * 10) / 10 : 0;
};

// Pulls TRS/TEI/LAI/CRI/ARS from this session's feedback + actionPlans responses.
const calculateMetricsFromFeedback = (session) => {
  const allActivities = session.subSessions?.flatMap(sub => sub.activities || []) || [];
  const q1 = [], q2 = [], q3 = [], q4 = [], q5 = [];
  const arsValues = [];

  allActivities.forEach(activity => {
    if (activity.type === 'feedback' && activity.responses?.length > 0) {
      activity.responses.forEach(r => {
        const resp = r.response || r;
        if (resp.topicRelevance) q1.push(resp.topicRelevance);
        if (resp.trainerEffectiveness) q2.push(resp.trainerEffectiveness);
        if (resp.confidenceToApply) q3.push(resp.confidenceToApply);
        if (resp.learningAdvocacy) q4.push(resp.learningAdvocacy);
        if (resp.challengeResolution) q5.push(resp.challengeResolution);
        if (resp.rating && !resp.topicRelevance) {
          q1.push(resp.rating); q2.push(resp.rating); q3.push(resp.rating);
          q4.push(resp.rating); q5.push(resp.rating);
        }
      });
    }
    if (activity.type === 'actionPlans' && activity.responses?.length > 0) {
      activity.responses.forEach(r => {
        const resp = r.response || r;
        if (resp.byWhen && resp.commitment) {
          const urgencyWeights = {
            '7_days': 1.0, '15_days': 0.85, '30_days': 0.70,
            '60_days': 0.45, '90_days': 0.25, 'not_sure': 0.15,
          };
          const uw = urgencyWeights[resp.byWhen] || 0.15;
          const cf = (resp.commitment || 1) / 10;
          arsValues.push(uw * cf);
        }
      });
    }
  });

  const avg = (arr) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
  return {
    trs: avg(q1), tei: avg(q2), pci: avg(q3),
    lai: avg(q4), cri: avg(q5), ars: avg(arsValues),
  };
};

const TrainerSessionAnalyticsPage = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    totalSessions: 0,
    activeSessions: 0,
    completedSessions: 0,
    scheduledSessions: 0,
    totalParticipants: 0,
    avgEngagement: 0,
    completionRate: 0,
    avgRating: 0,
  });
  const [sessionEffectiveness, setSessionEffectiveness] = useState([]);
  const [engagementByDay, setEngagementByDay] = useState([]);
  const [performanceIndices, setPerformanceIndices] = useState({
    topicRelevanceIndex: 0,
    actionReadinessIndex: 0,
    learningAdvocacyIndex: 0,
    trainerEffectivenessIndex: 0,
    challengeResolutionIndex: 0,
  });
  const [activityTotals, setActivityTotals] = useState({
    totalChallenges: 0,
    totalActionPlans: 0,
  });
  // ---- AI insight state (mirrors ClientAnalyticsPage) ----
  const [aiInsights, setAiInsights] = useState([]);
  const [capabilityGaps, setCapabilityGaps] = useState(null);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [recsLoading, setRecsLoading] = useState(false);
  const [aiHistory, setAiHistory] = useState([]);
  const [aiHistoryLoading, setAiHistoryLoading] = useState(false);
  // Track whether the user has actually clicked Load History yet, so the
  // empty state can differentiate "haven't loaded" from "loaded, no reports"
  // — without this, the same "Click Load History" message renders in both
  // states and the button looks broken when there are zero reports.
  const [aiHistoryLoaded, setAiHistoryLoaded] = useState(false);
  const [aiHistoryError, setAiHistoryError] = useState(null);
  const [selectedAiReport, setSelectedAiReport] = useState(null);

  useEffect(() => { fetchAnalyticsData(); }, [timeRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const days = periodToDays(timeRange);

      // sessionService.getSessions is already trainer-scoped server-side
      // (sessionController filters query.trainer = req.user._id when role === 'trainer').
      const allParams = { limit: 200 };
      const completedParams = { status: 'completed', limit: 200 };
      if (days != null) {
        allParams.days = days;
        completedParams.days = days;
      }

      const [allRes, completedRes] = await Promise.all([
        sessionService.getSessions(allParams),
        sessionService.getSessions(completedParams),
      ]);

      const allSessions = allRes.data || [];
      const completedSessions = completedRes.data || [];

      // Session counts by status — drives the pie + overview cards.
      const now = new Date();
      const isTrulyLive = (s) => {
        if (s.status !== 'live') return false;
        if (!s.endTime) return true;
        return new Date(s.endTime) >= now;
      };
      const liveCount = allSessions.filter(isTrulyLive).length;
      const scheduledCount = allSessions.filter(s => s.status === 'scheduled').length;
      const completedCount = allSessions.filter(s => s.status === 'completed').length;

      // Aggregate metrics across completed sessions.
      let totalEngagement = 0;
      let totalRating = 0;
      let ratingCount = 0;
      let totalCompletion = 0;
      let totalParticipants = 0;

      completedSessions.forEach(s => {
        totalParticipants += s.analytics?.totalParticipants || s.participants?.length || 0;
        const eng = calculateEngagement(s);
        const rating = calculateRating(s);
        totalEngagement += eng;
        totalCompletion += calculateCompletion(s);
        if (rating > 0) { totalRating += rating; ratingCount++; }
      });

      const completedN = completedSessions.length;
      const avgEngagement = completedN > 0 ? Math.round(totalEngagement / completedN) : 0;
      const avgRating = ratingCount > 0 ? Math.round((totalRating / ratingCount) * 10) / 10 : 0;
      const avgCompletion = completedN > 0 ? Math.round(totalCompletion / completedN) : 0;

      setAnalytics({
        totalSessions: allSessions.length,
        activeSessions: liveCount,
        completedSessions: completedCount,
        scheduledSessions: scheduledCount,
        totalParticipants,
        avgEngagement,
        completionRate: avgCompletion,
        avgRating,
      });

      // Top performing sessions (effectiveness = avg of engagement, completion, rating-as-percentage)
      const effectivenessData = completedSessions.map(s => {
        const participants = s.participants?.filter(p => p.status === 'joined').length || 0;
        const engagement = calculateEngagement(s);
        const completion = calculateCompletion(s);
        const rating = calculateRating(s);
        const ratingNormalized = (rating / 5) * 100;
        const metrics = [engagement, completion, ratingNormalized].filter(m => m > 0);
        const effectivenessScore = metrics.length > 0
          ? Math.round(metrics.reduce((a, b) => a + b, 0) / metrics.length)
          : 0;
        return {
          id: s._id,
          name: s.name || 'Unnamed Session',
          trainer: s.trainer ? `${s.trainer.firstName} ${s.trainer.lastName}` : 'You',
          participants,
          engagement,
          completion,
          rating,
          effectivenessScore,
        };
      }).sort((a, b) => b.effectivenessScore - a.effectivenessScore);
      setSessionEffectiveness(effectivenessData);

      // Engagement by day-of-week
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayBuckets = { Sun: [], Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [] };
      completedSessions.forEach(s => {
        const sessionDate = new Date(s.scheduledDate || s.createdAt);
        const dayName = dayNames[sessionDate.getDay()];
        dayBuckets[dayName].push(calculateEngagement(s));
      });
      setEngagementByDay(dayNames.map(day => ({
        day,
        avgEngagement: dayBuckets[day].length > 0
          ? Math.round(dayBuckets[day].reduce((a, b) => a + b, 0) / dayBuckets[day].length)
          : 0,
        sessionCount: dayBuckets[day].length,
      })));

      // Performance indices — prefer stored session.analytics, else compute from feedback.
      let totalTRS = 0, trsCount = 0;
      let totalTEI = 0, teiCount = 0;
      let totalLAI = 0, laiCount = 0;
      let totalCRI = 0, criCount = 0;
      let totalARS = 0, arsCount = 0;
      let totalChallenges = 0;
      let totalActionPlans = 0;

      completedSessions.forEach(session => {
        let m = {
          trs: session.analytics?.topicRelevanceIndex || 0,
          tei: session.analytics?.trainerEffectivenessIndex || 0,
          lai: session.analytics?.learningAdvocacyIndex || 0,
          cri: session.analytics?.challengeResolutionIndex || 0,
          ars: session.analytics?.actionReadinessIndex || 0,
        };
        const hasStored = m.trs > 0 || m.tei > 0 || m.lai > 0 || m.cri > 0 || m.ars > 0;
        if (!hasStored && session.subSessions?.length > 0) {
          m = calculateMetricsFromFeedback(session);
        }
        if (m.trs > 0) { totalTRS += m.trs; trsCount++; }
        if (m.tei > 0) { totalTEI += m.tei; teiCount++; }
        if (m.lai > 0) { totalLAI += m.lai; laiCount++; }
        if (m.cri > 0) { totalCRI += m.cri; criCount++; }
        if (m.ars > 0) { totalARS += m.ars; arsCount++; }

        const allActivities = session.subSessions?.flatMap(sub => sub.activities || []) || [];
        allActivities.forEach(activity => {
          if (activity.type === 'challenges' && activity.responses) {
            activity.responses.forEach(r => {
              const resp = r.response || r;
              if (resp.challenges && Array.isArray(resp.challenges)) {
                totalChallenges += resp.challenges.filter(c => c && c.trim()).length;
              } else if (resp.text || resp.challenge) {
                totalChallenges += 1;
              }
            });
          } else if (activity.type === 'actionPlans') {
            totalActionPlans += (activity.responses || []).length;
          }
        });
      });

      setPerformanceIndices({
        topicRelevanceIndex: trsCount > 0 ? Math.round((totalTRS / trsCount) * 100) / 100 : 0,
        actionReadinessIndex: arsCount > 0 ? Math.round((totalARS / arsCount) * 100) / 100 : 0,
        learningAdvocacyIndex: laiCount > 0 ? Math.round((totalLAI / laiCount) * 100) / 100 : 0,
        trainerEffectivenessIndex: teiCount > 0 ? Math.round((totalTEI / teiCount) * 100) / 100 : 0,
        challengeResolutionIndex: criCount > 0 ? Math.round((totalCRI / criCount) * 100) / 100 : 0,
      });
      setActivityTotals({ totalChallenges, totalActionPlans });

      // Generate heuristic data-driven insights from the metrics we just computed.
      setAiInsights(generateInsights({
        avgEngagement,
        avgRating,
        totalSessions: allSessions.length,
        completedSessions: completedCount,
      }, effectivenessData));
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const sessionStatusData = [
    { name: 'Completed', value: analytics.completedSessions, color: '#10b981' },
    { name: 'Live',      value: analytics.activeSessions,    color: '#f59e0b' },
    { name: 'Scheduled', value: analytics.scheduledSessions, color: '#3b82f6' },
  ];

  // Heuristic insight bullets — same logic ClientAnalyticsPage uses.
  function generateInsights(metrics, sessionData) {
    const out = [];
    const { avgEngagement, avgRating, totalSessions, completedSessions } = metrics;

    if (avgEngagement >= 80) {
      out.push({ type: 'success', title: 'Strong Engagement', description: `Average engagement is ${avgEngagement}% across your sessions. Great performance!` });
    } else if (avgEngagement >= 60) {
      out.push({ type: 'info', title: 'Good Engagement', description: `Average engagement is ${avgEngagement}%. Consider interactive activities to boost participation.` });
    } else if (avgEngagement > 0) {
      out.push({ type: 'warning', title: 'Low Engagement', description: `Average engagement is only ${avgEngagement}%. Review session content and delivery methods.` });
    }

    if (avgRating >= 4.5) {
      out.push({ type: 'success', title: 'Excellent Ratings', description: `Sessions have an average rating of ${avgRating}/5. Participants are highly satisfied.` });
    } else if (avgRating >= 3.5) {
      out.push({ type: 'info', title: 'Good Ratings', description: `Average session rating is ${avgRating}/5. Room for improvement in participant satisfaction.` });
    } else if (avgRating > 0) {
      out.push({ type: 'warning', title: 'Ratings Need Attention', description: `Average rating is ${avgRating}/5. Gather feedback to understand participant concerns.` });
    }

    if (totalSessions > 0) {
      const completionRatio = completedSessions / totalSessions;
      if (completionRatio >= 0.7) {
        out.push({ type: 'success', title: 'High Completion Rate', description: `${completedSessions} of ${totalSessions} sessions completed (${Math.round(completionRatio * 100)}%).` });
      } else {
        out.push({ type: 'info', title: 'Session Progress', description: `${completedSessions} of ${totalSessions} sessions have been completed so far.` });
      }
    }

    if (sessionData.length > 0 && sessionData[0].effectivenessScore >= 70) {
      out.push({ type: 'success', title: 'Top Performing Session', description: `"${sessionData[0].name}" scored ${sessionData[0].effectivenessScore} effectiveness.` });
    }

    if (out.length === 0) {
      out.push({ type: 'info', title: 'Getting Started', description: 'Complete more sessions to see detailed analytics and insights.' });
    }

    return out.slice(0, 4);
  }

  const getInsightIcon = (type) => {
    const icons = {
      success: (
        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      ),
      warning: (
        <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      info: (
        <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      ),
    };
    return icons[type] || icons.info;
  };

  const getInsightBg = (type) => ({
    success: 'bg-green-50 border-green-200',
    warning: 'bg-orange-50 border-orange-200',
    info: 'bg-blue-50 border-blue-200',
  }[type] || 'bg-blue-50 border-blue-200');

  const fetchAiHistory = async () => {
    setAiHistoryLoading(true);
    setAiHistoryError(null);
    try {
      const res = await api.get('/ai/reports/organization/history');
      if (res.success) {
        setAiHistory(res.data || []);
      } else {
        setAiHistoryError(res.error || 'Failed to load AI history');
      }
    } catch (err) {
      console.error('Error loading AI history:', err);
      setAiHistoryError(err.message || 'Failed to load AI history');
    } finally {
      setAiHistoryLoading(false);
      setAiHistoryLoaded(true);
    }
  };

  const runCapabilityGaps = async () => {
    setGapsLoading(true);
    try {
      const res = await api.get('/ai/analytics/capability-gaps');
      if (res.success && !res.data?.error) setCapabilityGaps(res.data);
      else setCapabilityGaps({ error: res.data?.error || 'AI not available' });
    } catch (err) {
      setCapabilityGaps({ error: err.message });
    } finally {
      setGapsLoading(false);
    }
  };

  const runRecommendations = async () => {
    setRecsLoading(true);
    try {
      const res = await api.get('/ai/analytics/recommendations');
      if (res.success && !res.data?.error) setRecommendations(res.data);
      else setRecommendations({ error: res.data?.error || 'AI not available' });
    } catch (err) {
      setRecommendations({ error: err.message });
    } finally {
      setRecsLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-4 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-3" />
            <p className="text-sm text-neutral-600">Loading analytics...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 space-y-4">
        {/* Back */}
        <div className="mb-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-neutral-600 hover:text-neutral-900 transition-colors group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-xl font-bold text-neutral-900">Session Analytics</h1>
            <p className="text-xs text-neutral-600">Insights and metrics across your training sessions</p>
          </div>

          <div className="relative inline-block">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="bg-white border border-neutral-200 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-neutral-700 hover:border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer"
              style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
            >
              {TIME_RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="w-4 h-4 text-neutral-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <StatCard label="Total Sessions"   value={analytics.totalSessions}            icon={LayoutGrid}     variant="blue" />
          <StatCard label="Avg Engagement"   value={`${analytics.avgEngagement}%`}      icon={TrendingUp}     variant="emerald" />
          <StatCard label="Session Rating"   value={analytics.avgRating}                icon={Star}           variant="amber" />
          <StatCard label="Total Challenges" value={activityTotals.totalChallenges}     icon={Target}         variant="rose" />
          <StatCard label="Action Plans"     value={activityTotals.totalActionPlans}    icon={ClipboardCheck} variant="violet" />
        </div>

        {/* Performance Indices */}
        <Card className="bg-gradient-to-br from-primary-50 to-blue-50 border-primary-200">
          <div className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <h3 className="text-sm font-semibold text-neutral-900">Performance Indices</h3>
              <span className="text-xs text-neutral-500 ml-auto">Aggregated from completed sessions</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {[
                { key: 'topicRelevanceIndex',       max: 5, label: 'Topic Relevance',      band: 'trs', interp: getTRSInterpretation, delay: 0.1 },
                { key: 'actionReadinessIndex',      max: 1, label: 'Action Readiness',     band: 'ars', interp: getARSInterpretation, delay: 0.2 },
                { key: 'learningAdvocacyIndex',     max: 5, label: 'Learning Advocacy',    band: 'lai', interp: getLAIInterpretation, delay: 0.3 },
                { key: 'trainerEffectivenessIndex', max: 5, label: 'Trainer Effectiveness',band: 'tei', interp: getTEIInterpretation, delay: 0.4 },
                { key: 'challengeResolutionIndex',  max: 5, label: 'Challenge Resolution', band: 'cri', interp: getCRIInterpretation, delay: 0.5 },
              ].map(idx => {
                const value = performanceIndices[idx.key];
                return (
                  <motion.div
                    key={idx.key}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx.delay }}
                    className="flex flex-col items-center"
                  >
                    {value > 0 ? (
                      <>
                        <GaugeMeter
                          value={value}
                          maxValue={idx.max}
                          label={idx.label}
                          band={getMetricBandInfo(idx.band, value).label}
                          color={getMetricBandInfo(idx.band, value).color}
                          size="sm"
                        />
                        <p className="mt-2 text-xs text-neutral-600 text-center max-w-[180px] leading-relaxed">
                          {idx.interp(value, 'trainer')}
                        </p>
                      </>
                    ) : (
                      <div className="text-center">
                        <div className="w-24 h-24 bg-neutral-100 rounded-full flex items-center justify-center mb-2">
                          <span className="text-neutral-400 text-sm">N/A</span>
                        </div>
                        <p className="text-xs font-medium text-neutral-700">{idx.label}</p>
                        <span className="text-xs text-neutral-400">No data</span>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Session Status + Data-Driven Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Session Status pie */}
          <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-4">
            <h3 className="text-sm font-semibold text-neutral-900 mb-3">Session Status</h3>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={sessionStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={60}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {sessionStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-2">
              {sessionStatusData.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-neutral-600">{item.name} ({item.value})</span>
                </div>
              ))}
            </div>
          </div>

          {/* Data-Driven Insights */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-neutral-100 p-4">
            <h3 className="text-sm font-semibold text-neutral-900 mb-3">Data-Driven Insights</h3>
            {aiInsights.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {aiInsights.map((insight, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex items-start gap-2 p-3 rounded-lg border ${getInsightBg(insight.type)}`}
                  >
                    {getInsightIcon(insight.type)}
                    <div>
                      <p className="text-xs font-semibold text-neutral-900">{insight.title}</p>
                      <p className="text-xs text-neutral-600 mt-0.5">{insight.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-xs text-neutral-500">Complete more sessions to generate insights</p>
              </div>
            )}
          </div>
        </div>

        {/* Top Performing Sessions */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-4">
          <h3 className="text-sm font-semibold text-neutral-900 mb-3">Top Performing Sessions</h3>
          <div className="overflow-x-auto">
            {sessionEffectiveness.length > 0 ? (
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Session</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Participants</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Engagement</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Completion</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Rating</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Effectiveness</th>
                  </tr>
                </thead>
                <tbody>
                  {sessionEffectiveness.map((session) => (
                    <tr key={session.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                      <td className="py-2 px-3 text-xs font-medium text-neutral-900">{session.name}</td>
                      <td className="py-2 px-3 text-xs text-neutral-600">{session.participants}</td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-neutral-200 rounded-full h-1.5 max-w-[60px]">
                            <div className="bg-green-500 h-1.5 rounded-full" style={{ width: `${session.engagement}%` }} />
                          </div>
                          <span className="text-xs font-medium text-neutral-900">{session.engagement}%</span>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-xs text-neutral-600">{session.completion}%</td>
                      <td className="py-2 px-3 text-xs text-neutral-600">{session.rating}/5</td>
                      <td className="py-2 px-3">
                        <Badge
                          variant={session.effectivenessScore >= 85 ? 'success' : session.effectivenessScore >= 70 ? 'warning' : 'error'}
                          size="sm"
                        >
                          {session.effectivenessScore}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-neutral-100 rounded-full mb-3">
                  <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <p className="text-xs font-medium text-neutral-900">No completed sessions yet</p>
                <p className="text-xs text-neutral-500 mt-1">Complete sessions to see performance data</p>
              </div>
            )}
          </div>
        </div>

        {/* Engagement by Day of Week */}
        {engagementByDay.length > 0 && engagementByDay.some(d => d.sessionCount > 0) && (() => {
          const totalSessions = engagementByDay.reduce((sum, d) => sum + d.sessionCount, 0);
          const avgEngagement = totalSessions > 0
            ? Math.round(engagementByDay.reduce((sum, d) => sum + d.avgEngagement * d.sessionCount, 0) / totalSessions)
            : 0;
          const bestDay = engagementByDay.reduce(
            (best, d) => d.avgEngagement > best.avgEngagement ? d : best,
            engagementByDay[0]
          );
          return (
            <div className="bg-white rounded-2xl border border-neutral-100 p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-500 flex items-center justify-center">
                    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-neutral-900">Engagement by Day of Week</span>
                </div>
                <span className="text-xs font-medium text-neutral-500 px-3 py-1.5 bg-neutral-100 rounded-lg">Weekly</span>
              </div>
              <div className="mb-1">
                <span className="text-3xl font-bold text-neutral-900">{avgEngagement}%</span>
              </div>
              <div className="flex items-center gap-2 mb-5">
                {bestDay.avgEngagement > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">
                    {bestDay.day} &#8599;
                  </span>
                )}
                <span className="text-xs text-neutral-500">
                  {totalSessions} session{totalSessions !== 1 ? 's' : ''} total
                </span>
              </div>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={engagementByDay} barCategoryGap="25%">
                    <defs>
                      <linearGradient id="trainerEngagementBarGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.15} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#9CA3AF', fontWeight: 500 }} axisLine={false} tickLine={false} />
                    <YAxis hide />
                    <Tooltip
                      contentStyle={{
                        borderRadius: '12px',
                        border: '1px solid #F3F4F6',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                        padding: '8px 12px',
                      }}
                      formatter={(value, name, props) => {
                        const sessionCount = props.payload?.sessionCount || 0;
                        return [`${value}% (${sessionCount} session${sessionCount !== 1 ? 's' : ''})`, 'Avg Engagement'];
                      }}
                      cursor={{ fill: 'transparent' }}
                    />
                    <Bar
                      dataKey="avgEngagement"
                      fill="url(#trainerEngagementBarGradient)"
                      radius={[8, 8, 4, 4]}
                      maxBarSize={40}
                    >
                      <LabelList
                        dataKey="avgEngagement"
                        position="top"
                        formatter={(val) => val > 0 ? `${val}%` : ''}
                        style={{ fontSize: 11, fontWeight: 600, fill: '#10b981' }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          );
        })()}

        {/* AI Capability Gap Insights + Training Recommendations */}
        <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-neutral-900">AI Capability Gap Insights</h3>
                <p className="text-xs text-neutral-500">AI-powered analysis of skill gaps across your sessions</p>
              </div>
            </div>
            <div className="flex gap-2">
              {!capabilityGaps && (
                <button
                  onClick={runCapabilityGaps}
                  disabled={gapsLoading}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-purple-600 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                  {gapsLoading ? 'Analyzing...' : 'Analyze Gaps'}
                </button>
              )}
              {!recommendations && capabilityGaps && !capabilityGaps.error && (
                <button
                  onClick={runRecommendations}
                  disabled={recsLoading}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                >
                  {recsLoading ? 'Generating...' : 'Get Recommendations'}
                </button>
              )}
            </div>
          </div>

          {gapsLoading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-3" />
              <p className="text-sm text-purple-600 font-medium">AI is analyzing data across your sessions...</p>
              <p className="text-xs text-neutral-400 mt-1">This may take 15-30 seconds</p>
            </div>
          )}

          {capabilityGaps?.error && (
            <div className="text-center py-6">
              <p className="text-sm text-neutral-500 mb-3">{typeof capabilityGaps.error === 'string' ? capabilityGaps.error : 'AI is not configured. Add an API key in environment variables.'}</p>
              <button onClick={() => setCapabilityGaps(null)} className="px-4 py-1.5 bg-neutral-100 text-neutral-700 rounded-lg text-xs font-medium hover:bg-neutral-200">Dismiss</button>
            </div>
          )}

          {capabilityGaps && !capabilityGaps.error && (() => {
            // Backend returns { gaps, strengths, immediateActions, note? }.
            const gaps = capabilityGaps.gaps || [];
            const strengths = capabilityGaps.strengths || [];
            const immediateActions = capabilityGaps.immediateActions || [];
            const noContent = gaps.length === 0 && strengths.length === 0 && immediateActions.length === 0;
            return (
              <div className="space-y-4">
                {capabilityGaps.note && (
                  <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                    <p className="text-sm text-purple-900 leading-relaxed">{capabilityGaps.note}</p>
                  </div>
                )}

                {(strengths.length > 0 || immediateActions.length > 0) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {strengths.length > 0 && (
                      <div className="p-3 bg-green-50 rounded-xl border border-green-100">
                        <p className="text-xs text-green-700 font-bold mb-2">Strengths</p>
                        {strengths.map((s, i) => (
                          <div key={i} className="flex items-start gap-2 mb-1.5">
                            <svg className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                            <p className="text-sm text-green-800">{s}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {immediateActions.length > 0 && (
                      <div className="p-3 bg-amber-50 rounded-xl border border-amber-100">
                        <p className="text-xs text-amber-700 font-bold mb-2">Immediate Actions</p>
                        {immediateActions.map((a, i) => (
                          <div key={i} className="flex items-start gap-2 mb-1.5">
                            <svg className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" /></svg>
                            <p className="text-sm text-amber-800">{a}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {gaps.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-neutral-800 mb-3">Identified Skill Gaps</h4>
                    <div className="space-y-2">
                      {gaps.map((gap, i) => (
                        <div key={i} className="p-3 bg-white rounded-xl border border-neutral-200 hover:border-neutral-300 transition-colors">
                          <div className="flex items-start justify-between gap-3 mb-1.5">
                            <p className="text-sm font-semibold text-neutral-900">{gap.capability}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase flex-shrink-0 ${
                              gap.severity === 'high' ? 'bg-red-100 text-red-700' :
                              gap.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>{gap.severity}</span>
                          </div>
                          <p className="text-xs text-neutral-600 mb-1.5">{gap.evidence}</p>
                          {typeof gap.affectedCount === 'number' && gap.affectedCount > 0 && (
                            <p className="text-[11px] text-neutral-500 mb-1.5">{gap.affectedCount} participant{gap.affectedCount === 1 ? '' : 's'} affected</p>
                          )}
                          {gap.recommendation && (
                            <div className="flex items-start gap-1.5 p-2 bg-blue-50 rounded-lg mt-2">
                              <svg className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" /></svg>
                              <p className="text-xs text-blue-800">{gap.recommendation}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {noContent && (
                  <div className="text-center py-6">
                    <p className="text-sm text-neutral-500">{capabilityGaps.note || 'Not enough post-session data yet to surface gaps.'}</p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Training Recommendations */}
          {recsLoading && (
            <div className="text-center py-6 mt-4 border-t border-neutral-100">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
              <p className="text-sm text-blue-600 font-medium">Generating training recommendations...</p>
            </div>
          )}

          {recommendations?.error && (
            <div className="text-center py-4 mt-4 border-t border-neutral-100">
              <p className="text-sm text-neutral-500">{typeof recommendations.error === 'string' ? recommendations.error : 'Could not generate recommendations'}</p>
              <button onClick={() => setRecommendations(null)} className="mt-2 px-4 py-1.5 bg-neutral-100 text-neutral-700 rounded-lg text-xs font-medium hover:bg-neutral-200">Dismiss</button>
            </div>
          )}

          {recommendations && !recommendations.error && (() => {
            // Backend returns { recommendations, sequencing, note? }.
            const recs = recommendations.recommendations || [];
            const sequencing = recommendations.sequencing || '';
            return (
              <div className="mt-4 pt-4 border-t border-neutral-100 space-y-4">
                <h4 className="text-sm font-bold text-neutral-900 flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342" />
                  </svg>
                  Recommended Training Programs
                </h4>

                {sequencing && (
                  <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-xs text-blue-700 font-bold mb-1">Suggested Sequencing</p>
                    <p className="text-sm text-blue-900">{sequencing}</p>
                  </div>
                )}

                {recs.length > 0 ? (
                  <div className="space-y-2">
                    {recs.map((rec, i) => (
                      <div key={i} className="p-3 bg-white rounded-xl border border-neutral-200">
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <p className="text-sm font-semibold text-neutral-900">{rec.title}</p>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase flex-shrink-0 ${
                            rec.priority === 'high' ? 'bg-red-100 text-red-700' :
                            rec.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-green-100 text-green-700'
                          }`}>{rec.priority}</span>
                        </div>
                        <p className="text-xs text-neutral-600 mb-1">{rec.rationale}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {rec.targetAudience && (
                            <span className="inline-flex items-center gap-1 text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded-full">
                              {rec.targetAudience}
                            </span>
                          )}
                          {rec.expectedOutcome && (
                            <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">
                              {rec.expectedOutcome}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500 text-center py-3">{recommendations.note || 'No additional training programs recommended at this time.'}</p>
                )}
              </div>
            );
          })()}
          )}

          {!capabilityGaps && !gapsLoading && (
            <div className="text-center py-6">
              <p className="text-sm text-neutral-500">Click "Analyze Gaps" to run AI analysis across your sessions</p>
            </div>
          )}
        </div>

        {/* AI Report History */}
        <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-bold text-neutral-900">AI Report History</h3>
                <p className="text-xs text-neutral-500">Previously generated AI session impact reports</p>
              </div>
            </div>
            <button
              onClick={fetchAiHistory}
              disabled={aiHistoryLoading}
              className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-indigo-600 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {aiHistoryLoading ? 'Loading...' : 'Load History'}
            </button>
          </div>

          {selectedAiReport && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  {new Date(selectedAiReport.createdAt).toLocaleString('en-IN')}
                  {selectedAiReport.generatedBy && ` by ${selectedAiReport.generatedBy}`}
                  {selectedAiReport.sessionName && ` | ${selectedAiReport.sessionName}`}
                </div>
                <button onClick={() => setSelectedAiReport(null)} className="text-xs text-primary-600 hover:text-primary-700 font-medium">Back to List</button>
              </div>
              <div className="space-y-3">
                {selectedAiReport.executiveSummary && (
                  <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                    <p className="text-xs text-indigo-600 font-bold mb-1">Executive Summary</p>
                    <p className="text-sm text-indigo-900 leading-relaxed">{selectedAiReport.executiveSummary}</p>
                  </div>
                )}
                {selectedAiReport.trainingEffectiveness && (
                  <div className="p-3 bg-neutral-50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold text-neutral-700">Training Effectiveness</p>
                      <Badge variant={selectedAiReport.trainingEffectiveness.overallScore === 'excellent' || selectedAiReport.trainingEffectiveness.overallScore === 'high' ? 'success' : selectedAiReport.trainingEffectiveness.overallScore === 'moderate' ? 'warning' : 'secondary'}>
                        {selectedAiReport.trainingEffectiveness.overallScore}
                      </Badge>
                    </div>
                    <p className="text-xs text-neutral-600">{selectedAiReport.trainingEffectiveness.evidence}</p>
                  </div>
                )}
                {selectedAiReport.strengths?.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <p className="text-xs text-green-700 font-bold mb-1">Strengths</p>
                      {selectedAiReport.strengths.map((s, i) => <p key={i} className="text-xs text-green-800 mb-0.5">- {s}</p>)}
                    </div>
                    {selectedAiReport.improvements?.length > 0 && (
                      <div className="p-2 bg-amber-50 rounded-lg">
                        <p className="text-xs text-amber-700 font-bold mb-1">Improvements</p>
                        {selectedAiReport.improvements.map((s, i) => <p key={i} className="text-xs text-amber-800 mb-0.5">- {s}</p>)}
                      </div>
                    )}
                  </div>
                )}
                {selectedAiReport.recommendations?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-neutral-700 mb-1.5">Recommendations</p>
                    {selectedAiReport.recommendations.map((r, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 bg-white rounded-lg border border-neutral-200 mb-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${r.priority === 'high' ? 'bg-red-100 text-red-700' : r.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{r.priority}</span>
                        <div>
                          <p className="text-xs text-neutral-900 font-medium">{r.recommendation}</p>
                          {r.expectedImpact && <p className="text-[11px] text-neutral-500">{r.expectedImpact}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {!selectedAiReport && aiHistory.length > 0 && (
            <div className="space-y-2">
              {aiHistory.map((report) => (
                <button
                  key={report._id}
                  onClick={async () => {
                    try {
                      const res = await api.get(`/ai/reports/${report._id}`);
                      if (res.success) setSelectedAiReport({ ...res.data, sessionName: report.sessionName });
                    } catch (err) {
                      toast.error('Failed to load report');
                    }
                  }}
                  className="w-full flex items-start gap-3 p-3 bg-neutral-50 hover:bg-neutral-100 rounded-xl border border-neutral-200 transition-colors text-left"
                >
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-neutral-900">{report.sessionName || 'Session Report'}</p>
                      <svg className="w-4 h-4 text-neutral-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                    </div>
                    <p className="text-xs text-neutral-500">
                      {new Date(report.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {' at '}
                      {new Date(report.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      {report.generatedBy && ` | By ${report.generatedBy}`}
                    </p>
                    {report.summary && <p className="text-xs text-neutral-400 mt-0.5 line-clamp-1">{report.summary}</p>}
                  </div>
                </button>
              ))}
            </div>
          )}

          {!selectedAiReport && aiHistory.length === 0 && !aiHistoryLoading && (
            <div className="text-center py-4">
              {aiHistoryError ? (
                <p className="text-sm text-red-500">{aiHistoryError}</p>
              ) : aiHistoryLoaded ? (
                <p className="text-sm text-neutral-400">No AI reports generated yet. Run sessions and generate impact reports to see them here.</p>
              ) : (
                <p className="text-sm text-neutral-400">Click "Load History" to see previously generated AI reports</p>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TrainerSessionAnalyticsPage;
